import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, buildQuery, errorMessage, type Customer, type CustomerUser, type License, type Page, type Product } from '../lib/api';
import { useAsync, useDebounced } from '../lib/hooks';
import { fmtDate } from '../lib/format';
import { ActionButton, ErrorAlert, Field, LastSeen, LicenseStatusBadge, Loading, Modal, PageHeader, Pagination, Spinner, TypeBadge } from '../components/ui';
import { CreateLicenseModal } from './Licenses';
import { useT } from '../i18n';

export function CustomersPage() {
  const t = useT();
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const list = useAsync(() => api.get<Page<Customer>>(`/v1/admin/customers${buildQuery({ q: dq, page, page_size: 25 })}`), [dq, page]);
  return (
    <>
      <PageHeader
        title={t('customers.title')}
        subtitle={t('customers.subtitle')}
        actions={<button className="btn btn-primary" onClick={() => setCreating(true)}>{t('customers.new')}</button>}
      />
      <ErrorAlert error={list.error} />
      <div className="card">
        <div className="toolbar">
          <input className="grow" placeholder={t('customers.searchPlaceholder')} value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
          {list.loading && <Spinner />}
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('common.name')}</th>
                <th>{t('common.email')}</th>
                <th>{t('customers.colTaxId')}</th>
                <th>{t('common.country')}</th>
                <th>{t('common.created')}</th>
              </tr>
            </thead>
            <tbody>
              {list.data?.items.length === 0 && <tr><td colSpan={5} className="empty">{t('customers.empty')}</td></tr>}
              {list.data?.items.map((c) => (
                <tr key={c.id}>
                  <td><Link to={`/clientes/${c.id}`}>{c.name}</Link></td>
                  <td>{c.email ?? '—'}</td>
                  <td>{c.tax_id ?? '—'}</td>
                  <td>{c.country ?? '—'}</td>
                  <td>{fmtDate(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {list.data && <Pagination page={page} pageSize={25} total={list.data.total} onPage={setPage} />}
      </div>
      {creating && <CustomerModal onClose={() => setCreating(false)} />}
    </>
  );
}

function CustomerModal({ customer, onClose, onSaved }: { customer?: Customer; onClose: () => void; onSaved?: () => Promise<void> }) {
  const t = useT();
  const navigate = useNavigate();
  const [f, setF] = useState({
    name: customer?.name ?? '',
    email: customer?.email ?? '',
    tax_id: customer?.tax_id ?? '',
    country: customer?.country ?? '',
    external_ref: customer?.external_ref ?? '',
    notes: customer?.notes ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v === '' && k !== 'name' ? null : v]));
    try {
      if (customer) {
        await api.patch(`/v1/admin/customers/${customer.id}`, body);
        await onSaved?.();
      } else {
        const c = await api.post<Customer>('/v1/admin/customers', body);
        navigate(`/clientes/${c.id}`);
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={customer ? t('customers.editTitle') : t('customers.newTitle')} onClose={onClose}>
      <ErrorAlert error={error} />
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label={t('customers.legalName')} full><input required value={f.name} onChange={set('name')} /></Field>
          <Field label={t('customers.contactEmail')}><input type="email" value={f.email} onChange={set('email')} /></Field>
          <Field label={t('customers.taxId')}><input value={f.tax_id} onChange={set('tax_id')} /></Field>
          <Field label={t('common.country')}><input value={f.country} onChange={set('country')} placeholder="ES" /></Field>
          <Field label={t('customers.externalRef')} help={t('customers.externalRefHelp')}><input value={f.external_ref} onChange={set('external_ref')} /></Field>
          <Field label={t('common.notes')} full><textarea value={f.notes} onChange={set('notes')} /></Field>
        </div>
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={busy}>{busy && <Spinner />} {t('common.save')}</button>
        </div>
      </form>
    </Modal>
  );
}

export function CustomerDetailPage() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const c = useAsync(() => api.get<Customer & { users: CustomerUser[]; licenses: License[] }>(`/v1/admin/customers/${id}`), [id]);
  const products = useAsync(() => api.get<Product[]>('/v1/admin/products'), []);
  const [editing, setEditing] = useState(false);
  const [creatingLicense, setCreatingLicense] = useState(false);
  const [user, setUser] = useState({ email: '', name: '' });
  const [error, setError] = useState<string | null>(null);
  if (!c.data) return c.error ? <ErrorAlert error={c.error} /> : <Loading />;
  const customer = c.data;
  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await c.reload();
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  return (
    <>
      <PageHeader
        crumb={<Link to="/clientes">{t('customers.back')}</Link>}
        title={customer.name}
        subtitle={customer.email ?? undefined}
        actions={
          <>
            <button className="btn" onClick={() => setEditing(true)}>{t('common.edit')}</button>
            <button className="btn btn-primary" onClick={() => setCreatingLicense(true)}>{t('customers.newLicense')}</button>
          </>
        }
      />
      <ErrorAlert error={error} />
      <div className="grid grid-2">
        <section className="card">
          <h2>{t('customers.data')}</h2>
          <dl className="props">
            <dt>{t('customers.taxId')}</dt><dd>{customer.tax_id ?? '—'}</dd>
            <dt>{t('common.country')}</dt><dd>{customer.country ?? '—'}</dd>
            <dt>{t('customers.externalRef')}</dt><dd>{customer.external_ref ?? '—'}</dd>
            <dt>{t('common.created')}</dt><dd>{fmtDate(customer.created_at)}</dd>
            <dt>{t('common.notes')}</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{customer.notes ?? '—'}</dd>
          </dl>
        </section>
        <section className="card">
          <h2>{t('customers.users')}</h2>
          <div className="table-wrap">
            <table>
              <tbody>
                {customer.users.length === 0 && <tr><td className="empty">{t('customers.noUsers')}</td></tr>}
                {customer.users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.name ?? '—'}</td>
                    <td>{u.email}</td>
                    <td className="num">
                      <ActionButton className="btn btn-sm btn-danger" confirm={t('customers.deleteUserConfirm')} onAction={() => run(() => api.del(`/v1/admin/customer-users/${u.id}`))}>
                        {t('common.delete')}
                      </ActionButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <form
            className="toolbar"
            style={{ marginTop: 12, marginBottom: 0 }}
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => {
                await api.post(`/v1/admin/customers/${customer.id}/users`, { email: user.email, name: user.name || null });
                setUser({ email: '', name: '' });
              });
            }}
          >
            <input type="email" required placeholder={t('customers.emailPlaceholder')} value={user.email} onChange={(e) => setUser({ ...user, email: e.target.value })} />
            <input className="grow" placeholder={t('common.name')} value={user.name} onChange={(e) => setUser({ ...user, name: e.target.value })} />
            <button className="btn">{t('common.add')}</button>
          </form>
        </section>
      </div>
      <section className="card" style={{ marginTop: 16 }}>
        <h2>{t('customers.licenses', { n: customer.licenses.length })}</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('customers.colKey')}</th>
                <th>{t('common.product')}</th>
                <th>{t('common.type')}</th>
                <th>{t('customers.colExpires')}</th>
                <th>{t('customers.colLastSeen')}</th>
                <th>{t('common.status')}</th>
              </tr>
            </thead>
            <tbody>
              {customer.licenses.length === 0 && <tr><td colSpan={6} className="empty">{t('customers.noLicenses')}</td></tr>}
              {customer.licenses.map((l) => (
                <tr key={l.id}>
                  <td><Link className="key" to={`/licencias/${l.id}`}>{l.key}</Link></td>
                  <td>{l.product_name}</td>
                  <td><TypeBadge type={l.type} mode={l.mode} seats={l.seats} /></td>
                  <td>{fmtDate(l.expires_at)}</td>
                  <td><LastSeen at={l.last_seen_at} offlineOnly={l.offline_only} /></td>
                  <td><LicenseStatusBadge status={l.status} expiresAt={l.expires_at} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {editing && <CustomerModal customer={customer} onClose={() => setEditing(false)} onSaved={async () => { setEditing(false); await c.reload(); }} />}
      {creatingLicense && products.data && <CreateLicenseModal products={products.data} defaultCustomerId={customer.id} onClose={() => setCreatingLicense(false)} />}
    </>
  );
}
